import React, { SetStateAction, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { CheckSquare, Pen, Trash } from 'react-bootstrap-icons';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AuthService } from '../../../authentification/login/service';
import ConfirmationModal from '../../../shared/components/ConfirmationModal';
import EmptyTable from '../../../shared/components/EmptyTable';
import {
  EChangeTarget,
  EChangeTargetStatus,
} from '../../../shared/enums/EChangeTarget';
import { formatDate } from '../../../shared/functions/formatDate';
import {
  needsFrcInspection,
  needsTechInspection,
} from '../../../shared/functions/getTruckReminder';
import { handleError } from '../../../shared/functions/handleError';
import { ITrailer } from '../../../shared/interfaces/ITrailer';
import { AdminTrailerService } from '../service';
import TrailerPreview from './TrailerPreview';

interface IProps {
  trailers: ITrailer[];
  isAdmin?: boolean;
  reset: UseFormReset<ITrailer>;
  setIsActionModalOpened: React.Dispatch<SetStateAction<boolean>>;
  handleUpdateList: () => void;
}

const TrailerTable = ({
  trailers,
  reset,
  isAdmin,
  setIsActionModalOpened,
  handleUpdateList,
}: IProps) => {
  const { t } = useTranslation();
  const [isPreviewModalOpened, setIsPreviewModalOpened] = useState(false);
  const [isDeleteOpened, setIsDeleteOpened] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState<ITrailer | undefined>(
    undefined,
  );
  const handleCancelDelete = () => {
    setSelectedTrailer(undefined);
    setIsDeleteOpened(false);
  };
  const handleConfirmDelete = async () => {
    !!selectedTrailer?.id &&
      (await AdminTrailerService.delete(selectedTrailer?.id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            handleCancelDelete();
            toast.success(
              t('toast.successDelete', { name: selectedTrailer?.licensePlate }),
            );
          }
        })
        .catch(handleError));
  };
  const handleClosePreviewModal = () => {
    setSelectedTrailer(undefined);
    setIsPreviewModalOpened(false);
  };
  const handleConfirmPreview = async (status: EChangeTargetStatus) => {
    await AuthService.updateChangeTarget({
      status,
      targetId: String(selectedTrailer?.id),
      target: EChangeTarget.TRAILER,
    })
      .then((response) => {
        if (response) {
          handleUpdateList();
          handleClosePreviewModal();
          toast.success(
            t('toast.successUpdated', { name: selectedTrailer?.licensePlate }),
          );
        }
      })
      .catch(handleError);
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table ">
          <thead className="table-dark">
            <tr>
              <th scope="col">#</th>
              <th scope="col">{t('general.licensePlate')}</th>
              <th scope="col">{t('general.frc')}</th>
              <th scope="col">{t('general.technicalInspection')}</th>
              <th scope="col">{t('general.actions')}</th>
              <th scope="col">{t('general.actions')}</th>
            </tr>
          </thead>
          <tbody className={'border'}>
            {!!trailers?.length ? (
              trailers?.map((trailer) => {
                return (
                  <tr key={trailer?.id}>
                    <td
                      className={
                        trailer?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {trailer?.id}
                    </td>
                    <td
                      className={
                        trailer?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {trailer?.licensePlate}
                    </td>
                    <td
                      className={
                        trailer?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatDate(trailer?.frc as string)}
                    </td>
                    <td
                      className={
                        trailer?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatDate(trailer?.technicalInspection)}
                    </td>

                    <td
                      className={
                        trailer?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      <div className="d-flex flex-wrap">
                        {needsTechInspection(trailer) && (
                          <div className="badge bg-warning text-dark me-1 mb-1 truck-badge"></div>
                        )}
                        {needsFrcInspection(trailer) && (
                          <div className="badge bg-secondary text-dark me-1 mb-1 truck-badge"></div>
                        )}
                      </div>
                    </td>
                    <td
                      className={
                        trailer?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        <Button
                          variant={'warning'}
                          size={'sm'}
                          onClick={() => {
                            reset(trailer);
                            setIsActionModalOpened(true);
                          }}
                        >
                          <Pen />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              variant={'danger'}
                              className={'mx-2'}
                              size={'sm'}
                              onClick={() => {
                                setSelectedTrailer(trailer);
                                setIsDeleteOpened(true);
                              }}
                            >
                              <Trash />
                            </Button>
                          </>
                        )}
                        {trailer?.hasPendingChange && (
                          <Button
                            variant={'primary'}
                            className={'mx-2'}
                            size={'sm'}
                            onClick={() => {
                              setSelectedTrailer(trailer);
                              setIsPreviewModalOpened(true);
                            }}
                          >
                            <CheckSquare />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <EmptyTable columns={6} />
            )}
          </tbody>
        </table>
      </div>
      <div className="mb-3">
        <span className="badge bg-warning text-white">
          {t('general.inspectionSoon')}
        </span>
        <span className="badge bg-secondary text-white mx-2">
          {t('general.frcSoon')}
        </span>
        <span className="badge bg-warning-subtle text-dark">
          {t('general.confirmationMissing')}
        </span>
      </div>

      <ConfirmationModal
        name={selectedTrailer?.licensePlate}
        isActionModalOpened={isDeleteOpened}
        handleConfirmDeletion={handleConfirmDelete}
        handleCloseActionModal={handleCancelDelete}
      />
      <Modal
        show={isPreviewModalOpened}
        onHide={handleClosePreviewModal}
        size={'lg'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('general.preview')} "{selectedTrailer?.licensePlate}"
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TrailerPreview trailerId={Number(selectedTrailer?.id)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePreviewModal}>
            {t('button.close')}
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="danger"
                onClick={() =>
                  handleConfirmPreview(EChangeTargetStatus.REJECTED)
                }
              >
                {t('button.decline')}
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  handleConfirmPreview(EChangeTargetStatus.APPROVED)
                }
              >
                {t('button.accept')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default TrailerTable;
